import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { colors } from '../../utils/colors';
import { SectionTitle } from '../common';

const FaqSection = styled.section`
  padding: 6rem 1rem;
  background: ${colors.backgroundLight};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.border}, transparent);
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FaqList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FaqItem = styled(motion.div)`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 4px 20px ${colors.shadow};
  overflow: hidden;
`;

const Question = styled.dt``;

const QuestionButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: 1rem;

  span {
    font-size: 1.1rem;
    font-weight: 600;
    color: ${colors.textDark};
    line-height: 1.4;
  }

  i {
    color: ${colors.primary};
    font-size: 1rem;
    flex-shrink: 0;
    transition: transform 0.3s ease;
  }

  &:hover span {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;

    span {
      font-size: 1rem;
    }
  }
`;

const Answer = styled(motion.dd)`
  margin: 0;
  padding: 0 2rem 1.5rem;
  color: ${colors.textLight};
  font-size: 1rem;
  line-height: 1.8;
  border-top: 1px solid ${colors.border};
  padding-top: 1.25rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.25rem;
  }
`;

const faqs = [
  {
    question: '¿Qué es la terapia cognitivo-conductual?',
    answer: 'La terapia cognitivo-conductual (TCC) es un enfoque psicológico basado en la evidencia que trabaja la relación entre pensamientos, emociones y comportamientos. A través de técnicas prácticas, aprendemos a identificar y modificar patrones de pensamiento negativos que generan malestar, reemplazándolos por formas más adaptativas de ver e interpretar la realidad.'
  },
  {
    question: '¿Para qué sirve el EMDR?',
    answer: 'El EMDR (Desensibilización y Reprocesamiento por Movimientos Oculares) es una terapia especializada para procesar experiencias traumáticas o perturbadoras que quedaron "atascadas" en el sistema nervioso. Es muy efectiva para el tratamiento del estrés post-traumático, accidentes, duelos complicados, fobias y situaciones de alta carga emocional. El objetivo es que esos recuerdos pierdan su carga perturbadora y puedan integrarse de forma sana.'
  },
  {
    question: '¿Atendés de forma online?',
    answer: 'Sí, ofrezco atención tanto presencial en mi consultorio en Pocitos, Montevideo, como online por videollamada para cualquier persona en Uruguay o en el exterior. Las sesiones online tienen la misma calidad y efectividad que las presenciales, con la ventaja de la comodidad y flexibilidad de conectarte desde donde estés.'
  },
  {
    question: '¿Cuántas sesiones se necesitan?',
    answer: 'La cantidad de sesiones varía según cada persona, el motivo de consulta y los objetivos terapéuticos. Algunas personas notan cambios significativos en pocas semanas, mientras que procesos más profundos pueden llevar varios meses. En la primera consulta evaluamos juntos tu situación y acordamos una orientación sobre el proceso.'
  },
  {
    question: '¿Trabajás con adolescentes?',
    answer: 'Sí, atiendo adolescentes y adultos. El trabajo con adolescentes requiere un enfoque adaptado a su etapa de desarrollo, con un lenguaje cercano y técnicas adecuadas para su edad. También mantengo comunicación con la familia cuando es necesario y beneficioso para el proceso terapéutico.'
  },
  {
    question: '¿Cómo sé si necesito ir al psicólogo?',
    answer: 'Algunas señales que pueden indicar que es un buen momento para consultar: sensación de malestar emocional sostenido, dificultad para manejar el estrés o la ansiedad, tristeza o desmotivación que no pasa, dificultades en relaciones personales o laborales, experiencias difíciles no procesadas, o simplemente el deseo de conocerte mejor y crecer emocionalmente. No hace falta estar en crisis para buscar apoyo psicológico.'
  }
];

export const Faq = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <FaqSection id="faq" ref={ref}>
      <Container>
        <SectionTitle
          title="Preguntas Frecuentes"
          subtitle="Respuestas a las consultas más comunes sobre la terapia psicológica."
          centered

        />

        <FaqList>
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
            >
              <Question>
                <QuestionButton
                  onClick={() => toggle(index)}
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span>{faq.question}</span>
                  <i
                    className="fa-solid fa-chevron-down"
                    aria-hidden="true"
                    style={{ transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </QuestionButton>
              </Question>

              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <Answer
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    {faq.answer}
                  </Answer>
                )}
              </AnimatePresence>
            </FaqItem>
          ))}
        </FaqList>
      </Container>
    </FaqSection>
  );
};
